import { StyleSheet, Platform } from 'react-native'
import type { Theme } from '../../../src/theme/getTheme'

export const getLandingHeroSectionBgStyle = (theme: Theme, isDark: boolean) =>
  Platform.OS === 'web'
    ? ({
        background: isDark
          ? `linear-gradient(135deg, ${theme.background.primary} 0%, ${theme.background.shell} 55%, ${theme.background.card} 100%)`
          : 'linear-gradient(135deg, #F3EDFF 0%, #FAF5FF 40%, #EBF4FF 100%)',
      } as object)
    : undefined

export const landingHeroGradientTextStyle =
  Platform.OS === 'web'
    ? ({
        background: 'linear-gradient(135deg, #9F7AEA 0%, #6B46C1 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent',
      } as object)
    : undefined

export function getLandingHeroStyles(theme: Theme) {
  return StyleSheet.create({
  section: {
    position: 'relative',
    paddingTop: 80,
    paddingBottom: 60,
    paddingHorizontal: 24,
    overflow: 'hidden',
    backgroundColor: Platform.OS === 'web' ? 'transparent' : theme.background.primary,
  },
  sectionPhone: {
    paddingTop: 40,
    paddingBottom: 36,
    paddingHorizontal: 16,
  },
  bgBlob: {
    position: 'absolute',
    borderRadius: 999,
    opacity: 0.85,
  },
  bgBlobPrimary: {
    top: -120,
    right: -140,
    width: 520,
    height: 520,
    backgroundColor: theme.primary + '38',
  },
  bgBlobGold: {
    bottom: -170,
    left: -110,
    width: 420,
    height: 420,
    backgroundColor: theme.accent + '29',
  },
  container: {
    maxWidth: 1240,
    marginHorizontal: 'auto' as unknown as number,
    width: '100%',
    zIndex: 1,
  },
  grid: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    alignItems: 'center',
    gap: Platform.OS === 'web' ? 64 : 40,
  },
  gridStacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 38,
  },
  gridPhone: {
    gap: 26,
  },
  copy: {
    flex: Platform.OS === 'web' ? 1 : undefined,
    maxWidth: Platform.OS === 'web' ? 560 : undefined,
  },
  copyStacked: {
    maxWidth: 720,
    alignSelf: 'center',
    alignItems: 'center',
  },
  h1: {
    fontFamily:
      Platform.OS === 'web'
        ? '"Space Grotesk", "Inter", system-ui, sans-serif'
        : undefined,
    fontSize: Platform.OS === 'web' ? 60 : 36,
    fontWeight: '700',
    lineHeight: Platform.OS === 'web' ? 66 : 42,
    letterSpacing: -1.5,
    color: theme.gray[900],
    marginBottom: 20,
  },
  h1Stacked: {
    textAlign: 'center',
    fontSize: 48,
    lineHeight: 54,
  },
  h1Phone: {
    fontSize: 36,
    lineHeight: 41,
    letterSpacing: -1,
    marginBottom: 16,
  },
  h1Accent: {
    color: theme.primary,
  },
  h1Underline: {
    color: theme.gray[900],
    textDecorationLine: 'underline',
    textDecorationStyle: 'solid',
    textDecorationColor: theme.primaryLight,
  },
  lede: {
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 18,
    lineHeight: 28,
    color: theme.gray[600],
    marginBottom: 32,
  },
  ledeStacked: {
    textAlign: 'center',
    maxWidth: 620,
  },
  ledePhone: {
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 24,
  },
  ctaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 28,
  },
  ctaRowPhone: {
    width: '100%',
    justifyContent: 'center',
    marginBottom: 22,
  },
  ctaPressable: {
    borderRadius: 16,
  },
  ctaPressablePhone: {
    width: '100%',
  },
  btnPrimary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginVertical: 1,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 16,
    minHeight: 52,
    overflow: 'hidden',
  },
  btnPrimaryPhone: {
    width: '100%',
  },
  btnPrimaryShine: {
    position: 'absolute',
    left: 0,
    top: -18,
    width: 34,
    height: 88,
    backgroundColor: 'rgba(255, 255, 255, 0.38)',
  },
  btnPrimaryText: {
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    letterSpacing: -0.1,
  },
  storeBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    marginBottom: 28,
  },
  storeBadgesCentered: {
    justifyContent: 'center',
  },
  storeBadgePressable: {
    borderRadius: 10,
  },
  storeBadgeHovered: {
    opacity: 0.82,
  },
  /**
   * Explicit width+height (NOT aspectRatio): react-native-web renders Image as a
   * background-image div with no intrinsic size, so an aspectRatio-only badge can
   * collapse/stretch and wrap to the next line. Widths preserve each badge's native
   * ratio (App Store 431×144 → ≈3:1, Google Play 478×142 → ≈3.37:1) at height 52.
   */
  appStoreBadge: {
    width: 156,
    height: 52,
  },
  googlePlayBadge: {
    width: 175,
    height: 52,
  },
  /** Phone: same native ratios at height 44. */
  appStoreBadgePhone: {
    width: 132,
    height: 44,
  },
  googlePlayBadgePhone: {
    width: 148,
    height: 44,
  },
  trustRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  trustRowWhenStacked: {
    justifyContent: 'center',
    alignSelf: 'stretch',
  },
  trustPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: theme.background.cardElevated,
    paddingVertical: 7,
    paddingHorizontal: 13,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: theme.gray[400],
    shadowColor: theme.primaryDark,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.07,
    shadowRadius: 6,
    elevation: 2,
  },
  trustPillText: {
    fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
    fontSize: 13,
    fontWeight: '500',
    color: theme.gray[900],
  },
  trustPillPhone: {
    paddingVertical: 6,
    paddingHorizontal: 11,
    gap: 5,
  },
  trustPillTextPhone: {
    fontSize: 12,
    lineHeight: 15,
  },
  })
}
